import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CustomInputComponent } from '../../../shared/custom-input/custom-input.component';
import { CustomButtonComponent } from '../../../shared/custom-button/custom-button.component';
import { ValidationService } from '../../../services/validation/validation.service';
import { AuthService } from '../../../services/auth/auth.service';


@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomInputComponent,
    MatIconModule,
    MatButtonModule,
    CustomButtonComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ForgotPasswordComponent implements OnInit {
  ELEMENT_IDS: { [key: string]: string } = ELEMENT_IDS;
  @Input() fpData: any;

  formVerificationCode: FormGroup;
  formResetPassword: FormGroup;
  loading: boolean = false;
  alreadyHaveVerificationCode: boolean = false;
  resendCountdown: number = 0;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  passwordRequirements: any[] = [];
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder, 
    private router: Router,
    private authService: AuthService,
    public validationService: ValidationService,
  ) {
    this.passwordRequirements = validationService.passwordRequirements;

    this.formVerificationCode = this.fb.group({
      username: ['', Validators.required],
    });
    this.formResetPassword = this.fb.group({
      username: ['', Validators.required],
      verificationCode: ['', Validators.required],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.validationService.uppercaseLowercaseValidator(),
        this.validationService.numberValidator(),
        this.validationService.specialCharValidator(),
      ]],
      confirmPassword: ['', Validators.required],
      showPassword: false,
    }, { validators: this.validationService.passwordMatchValidator });

    this.formResetPassword.get('newPassword')?.valueChanges.subscribe(() => {
      this.validationService.updatePasswordRequirements(this.formResetPassword);
    });

    this.formResetPassword.valueChanges.subscribe(() => {
      this.validationService.updatePasswordRequirements(this.formResetPassword);
    });
  }

  ngOnInit(): void {
    if (this.fpData) {
      this.formVerificationCode.patchValue({ username: this.fpData?.username });
      this.formResetPassword.patchValue({ username: this.fpData?.username });
    }
  }

  onSendVerificationCode(resend: boolean = false): void {
    this.loading = true;
    const form = resend ? this.formResetPassword : this.formVerificationCode;
    if (form.controls['username'].valid) {
        this.authService.forgotPassword(form.controls['username'].value).then(
            (result) => {
                this.alreadyHaveVerificationCode = true;

                if (!resend) {
                    this.router.navigate(['/login'], {
                        queryParams: { mode: 'forgot-password', username: this.formVerificationCode.get('username')?.value },
                    });

                    this.formResetPassword.patchValue({ username: this.formVerificationCode.get('username')?.value });
                }

                this.loading = false;

                this.resendCountdown = resend ? 60 : 0;
                this.startResendCountdown();
            },
            (error) => {
                this.loading = false;
                this.resendCountdown = 0;
                console.error(error);
                if (error.name == 'UserNotFoundException') {
                    this.errorMessage = "Username not found";
                    form.controls['username'].setErrors({ required: true });
                } else {
                    this.errorMessage = error.message;
                }
            }
        );
    }
  }

  private startResendCountdown(): void {
    const timer = setInterval(() => {
        if (this.resendCountdown > 0) {
            this.resendCountdown--;
        } else {
            clearInterval(timer);
        }
    }, 1000);
  }

  onResetPassword() {
    this.loading = true;
    this.authService.confirmForgotPassword(
        this.formResetPassword.get('username')?.value,
        this.formResetPassword.get('verificationCode')?.value,
        this.formResetPassword.get('newPassword')?.value
    ).then(
        (result) => {
            this.loading = false;
            this.router.navigate(['/login'], {});
        },
        (error) => {
            this.loading = false;
            console.error(error);
            if (error.name == 'UserNotFoundException') {
                this.errorMessage = "Username not found";
                this.formResetPassword.controls['username'].setErrors({ required: true });
            } else if (error.name == 'CodeMismatchException') {
                this.errorMessage = error.message;
                this.formResetPassword.controls['verificationCode'].setErrors({ required: true });
            } else {
                this.errorMessage = error.message;
            }
        }
    )
  }

  onHasVerificationCode() {
    this.alreadyHaveVerificationCode = true;
  }

  onBackToLogin() {
    this.router.navigate(['/login'], {});
  }

  toggleShowPassword(whichPassword: 'new-password' | 'confirm-password') {
    if (whichPassword === 'new-password') {
      this.showNewPassword = !this.showNewPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
}

const BASE_ELEMENT_ID: string = 'id_forgot_password';
const ELEMENT_IDS: { [key: string]: string } = {
  page: `${BASE_ELEMENT_ID}_page`,
  hdr: `${BASE_ELEMENT_ID}_hdr`,
  lbl_title: `${BASE_ELEMENT_ID}_lbl_title`,
  lbl_subtitle: `${BASE_ELEMENT_ID}_lbl_subtitle`,

  form_request_code: `${BASE_ELEMENT_ID}_form_request_code`,
  input_username_request: `${BASE_ELEMENT_ID}_input_username_request`,
  btn_send_code: `${BASE_ELEMENT_ID}_btn_send_code`,
  btn_back_request: `${BASE_ELEMENT_ID}_btn_back_request`,
  msg_error_request: `${BASE_ELEMENT_ID}_msg_error_request`,

  form_reset: `${BASE_ELEMENT_ID}_form_reset`,
  input_username_reset: `${BASE_ELEMENT_ID}_input_username_reset`,
  input_verification_code: `${BASE_ELEMENT_ID}_input_verification_code`,
  lnk_resend_code: `${BASE_ELEMENT_ID}_lnk_resend_code`,
  input_new_password: `${BASE_ELEMENT_ID}_input_new_password`,
  btn_toggle_new_password: `${BASE_ELEMENT_ID}_btn_toggle_new_password`,
  input_confirm_password: `${BASE_ELEMENT_ID}_input_confirm_password`,
  btn_toggle_confirm_password: `${BASE_ELEMENT_ID}_btn_toggle_confirm_password`,
  msg_error_reset: `${BASE_ELEMENT_ID}_msg_error_reset`,
  lst_requirements: `${BASE_ELEMENT_ID}_lst_requirements`,
  lst_requirements_item: `${BASE_ELEMENT_ID}_lst_requirements_item`,
  lst_requirements_icon: `${BASE_ELEMENT_ID}_lst_requirements_icon`,
  lst_requirements_lbl: `${BASE_ELEMENT_ID}_lst_requirements_lbl`,
  btn_reset_password: `${BASE_ELEMENT_ID}_btn_reset_password`,
  btn_back_reset: `${BASE_ELEMENT_ID}_btn_back_reset`,
};
